import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Stack } from '@mui/material'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'
import { useConsentMutation, useGetUsersConsent } from '../../api/ester-b2b-api/private/consent/consentQueries'
import { CONSENT_VERSIONS, ConsentId, JournalConsentId } from '../../api/ester-b2b-api/private/consent/types'
import { QueryKeys } from '../../api/ester-b2b-api/types'
import { useSelectHasPersonalNumber, useSelectUserMarket } from '../../state/selectors'
import { theme } from '../../theme/theme'
import { isSwedishMarket } from '../../utils/market'
import { dataPolicyPath } from '../../views/policy/dataPolicyPath'
import { Checkbox } from '../inputs/Checkbox'
import { BodyMedium } from '../styles/Typography'

export const useJournalConsent = (consentId: JournalConsentId) => {
  const queryClient = useQueryClient()
  const { data: consentData, isLoading } = useGetUsersConsent(consentId)
  const { mutateAsync: postConsent } = useConsentMutation()
  const [checked, setChecked] = useState(false)

  const hasConsent = !!consentData && consentData.version === CONSENT_VERSIONS[consentId]

  const saveConsent = async () => {
    await postConsent(consentId)
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.Consent, consentId] })
  }

  return { hasConsent, isLoading, checked, setChecked, saveConsent }
}

export const useScreeningSummaryOptIn = () => {
  const userMarket = useSelectUserMarket()
  const hasPersonalNumber = useSelectHasPersonalNumber()
  const { hasConsent, isLoading, checked, setChecked, saveConsent } = useJournalConsent(
    ConsentId.SCREENING_SUMMARY_JOURNAL
  )

  const isSwedish = isSwedishMarket(userMarket)
  const showOptIn = isSwedish && !isLoading && !hasConsent
  const askForPersonalNumber = isSwedish && !hasPersonalNumber && (hasConsent || checked)

  const saveOptIn = async () => {
    if (showOptIn && checked) await saveConsent()
  }

  return { showOptIn, checked, setChecked, askForPersonalNumber, saveOptIn }
}

interface JournalConsentCheckboxProps {
  title?: string
  text: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

export const JournalConsentCheckbox = ({ title, text, label, checked, onChange }: JournalConsentCheckboxProps) => {
  const { t, i18n } = useTranslation()

  return (
    <ConsentContainer gap={`${theme.spacing.small}px`}>
      <BodyMedium>
        {title && <strong>{title} </strong>}
        {text}{' '}
        <Link to={dataPolicyPath(i18n.language, '#2')} target="_blank" rel="noopener noreferrer">
          {t('common.dataPolicy.readMore')}
        </Link>
      </BodyMedium>
      <Stack direction="row">
        <Checkbox
          onChange={() => onChange(!checked)}
          checked={checked}
          aria-label={label}
          borderColor={theme.color.grey3}
          backgroundColor={theme.color.white}
        />
        <LabelContainer>
          <BodyMedium>{label}</BodyMedium>
        </LabelContainer>
      </Stack>
    </ConsentContainer>
  )
}

export const OptionalJournalSection = styled(Stack)`
  gap: ${theme.spacing.mediumLarge}px;

  &:not(:first-child) {
    padding-top: ${theme.spacing.mediumLarge}px;
    border-top: 1px solid ${theme.color.grey2};
  }
`

const ConsentContainer = styled(Stack)`
  text-align: left;
  & p {
    color: ${theme.color.grey4};
  }
  & a {
    color: ${theme.color.plumMid};
    text-decoration: underline;
  }
`

const LabelContainer = styled.div`
  margin-left: ${theme.spacing.small}px;
`
